import { Clock, ArrowRight } from "lucide-react";
import { useLang } from "@/App";
import { all, pick } from "@/data/images";
import Hero from "@/components/Hero";
import { Reveal, Pretitulo, TituloSeccion } from "@/components/Reveal";

export default function InfoUtil() {
  const { t } = useLang();
  const p = t.infoUtil;

  return (
    <div data-testid="pagina-info-util">
      <Hero pre={p.hero.pre} titulo={p.hero.titulo} fotos={[pick(p.hero.foto[0], 0) || all(p.hero.foto[0])[0]]} testid="hero-info-util" />

      {/* Presentación */}
      <section className="py-20 md:py-24" data-testid="iu-intro">
        <div className="mx-auto max-w-3xl px-6 text-center md:px-10">
          <Reveal>
            <Pretitulo>{p.presentacion.pre}</Pretitulo>
            <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-ink md:text-4xl">{p.presentacion.titulo}</h2>
            <p className="mt-7 text-[15px] leading-relaxed text-ink-soft">{p.presentacion.cuerpo}</p>
          </Reveal>
        </div>
      </section>

      {/* Horarios */}
      <section className="bg-sand-deep py-20 md:py-24" data-testid="iu-horarios">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10">
          <TituloSeccion pre={p.horarios.pre} titulo={p.horarios.titulo} />
          <div className="grid gap-6 md:grid-cols-2">
            {p.horarios.cards.map((c, i) => (
              <Reveal key={c.t} delay={i * 0.1} className="border border-lineas bg-sand p-9" testid={`iu-horario-${i}`}>
                <div className="flex items-center gap-4">
                  <Clock size={26} strokeWidth={1.3} className="text-terra" />
                  <p className="font-serif text-3xl font-light text-ink md:text-4xl">{c.t}</p>
                </div>
                <p className="mt-5 text-sm leading-relaxed text-ink-soft">{c.d}</p>
                <p className="mt-5 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-terra">
                  <ArrowRight size={13} /> {c.nota}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Estadía y capacidad */}
      <section className="py-20 md:py-24" data-testid="iu-estadia">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <TituloSeccion pre={p.estadia.pre} titulo={p.estadia.titulo} />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-6 md:gap-6">
            {p.estadia.cards.map((c, i) => (
              <Reveal key={c.t} delay={i * 0.06} className={`border border-lineas bg-sand p-7 ${i < 2 ? "lg:col-span-3" : "lg:col-span-2"}`} testid={`iu-estadia-${i}`}>
                <h3 className="font-serif text-2xl font-light text-ink">{c.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ink-soft">{c.d}</p>
                <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.18em] text-terra">{c.nota}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Confort */}
      <section className="bg-sand-deep py-20 md:py-24" data-testid="iu-confort">
        <div className="mx-auto max-w-[1300px] px-6 md:px-10">
          <TituloSeccion pre={p.confort.pre} titulo={p.confort.titulo} />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5 md:gap-6">
            {p.confort.cards.map((c, i) => (
              <Reveal key={c.t} delay={(i % 5) * 0.06} className="border border-lineas bg-sand p-6" testid={`iu-confort-${i}`}>
                <h3 className="font-serif text-lg text-ink">{c.t}</h3>
                <p className="mt-2.5 text-[13px] leading-relaxed text-ink-soft">{c.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Ruta */}
      <section className="py-20 md:py-24" data-testid="iu-ruta">
        <div className="mx-auto max-w-[1300px] px-6 md:px-10">
          <TituloSeccion pre={p.ruta.pre} titulo={p.ruta.titulo} />
          <div className="grid gap-5 lg:grid-cols-2 md:gap-6">
            {p.ruta.cards.map((c, i) => (
              <Reveal key={c.t} delay={i * 0.06} className={`border border-lineas bg-sand-deep p-7 md:p-8 ${i === 0 ? "lg:col-span-2" : ""}`} testid={`iu-ruta-${i}`}>
                <h3 className="font-serif text-2xl font-light text-ink">{c.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ink-soft">{c.d}</p>
                {c.nota && <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.18em] text-terra">{c.nota}</p>}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Reglas */}
      <section className="bg-sand-deep py-20 md:py-24" data-testid="iu-reglas">
        <div className="mx-auto max-w-[1300px] px-6 md:px-10">
          <TituloSeccion pre={p.reglas.pre} titulo={p.reglas.titulo} />
          <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
            {[p.reglas.col1, p.reglas.col2].map((col, ci) => (
              <div key={ci}>
                <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-terra">{ci === 0 ? p.reglas.col1Titulo : p.reglas.col2Titulo}</p>
                <div className="mt-6 space-y-4">
                  {col.map((c, i) => (
                    <Reveal key={c.t} delay={i * 0.05} className="border border-lineas bg-sand p-6" testid={`iu-regla-${ci}-${i}`}>
                      <h3 className="font-serif text-lg text-ink">{c.t}</h3>
                      <p className="mt-2 text-[13px] leading-relaxed text-ink-soft">{c.d}</p>
                    </Reveal>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}